import { v4 as uuidv4 } from 'uuid'
import { BaseOfflineStore } from './base.store'
import { OfflinePatient } from '../types/database.types'
import { Patient } from '@/core/models/patient.model'
import { queuePatientForSync, queuePatientUpdateForSync } from '../utils/sync-queue.utils'
import { safelyConvertDateToString } from '../utils/date.utils'

type OfflinePatientCreatePayload = Omit<Patient, '_id' | 'userInfo'> & {
    userInfo: Omit<Patient['userInfo'], 'name' | 'surname'> & {
        name?: string
        surname?: string
    }
}

export class PatientOfflineStore extends BaseOfflineStore<OfflinePatient> {
    constructor() {
        super('patients')
    }

    async createOfflinePatient(patient: OfflinePatientCreatePayload): Promise<OfflinePatient> {
        const offlineId = uuidv4()
        const now = Date.now()

        const offlinePatient: OfflinePatient = {
            ...patient,
            _id: offlineId,
            _offlineId: offlineId,
            _localId: offlineId,
            _syncStatus: 'pending',
            _lastModified: now,
            _createdAt: now,
            // Ensure birthday is properly formatted
            userInfo: {
                ...patient.userInfo,
                name: patient.userInfo.name as string,
                surname: patient.userInfo.surname as string,
                birthday: safelyConvertDateToString(patient.userInfo.birthday) || patient.userInfo.birthday,
            },
        }

        const createdPatient = await this.add(offlinePatient)

        // Queue for sync
        try {
            await queuePatientForSync(createdPatient)
        } catch (error) {
            console.error('Failed to queue patient for sync:', error)
        }

        return createdPatient
    }

    async getByOfflineId(offlineId: string): Promise<OfflinePatient | undefined> {
        const patients = await this.getByIndex('_offlineId', offlineId)
        return patients[0]
    }

    async getBySyncStatus(status: 'synced' | 'pending' | 'failed'): Promise<OfflinePatient[]> {
        return this.getByIndex('_syncStatus', status)
    }

    async markAsSynced(patient: OfflinePatient, serverId: string): Promise<OfflinePatient> {
        const updatedPatient: OfflinePatient = {
            ...patient,
            // Don't change _id as it's the primary key - keep the offline ID
            // _id: serverId, // This line causes the IndexedDB update to fail
            _cloudId: serverId, // Store the server ID separately
            _syncStatus: 'synced',
            _lastModified: Date.now(),
        }

        return this.update(updatedPatient)
    }

    async updatePatient(patientId: string, updates: Partial<OfflinePatient>): Promise<OfflinePatient> {
        const patient = await this.get(patientId)
        if (!patient) {
            throw new Error(`Patient with ID ${patientId} not found`)
        }

        const updatedPatient: OfflinePatient = {
            ...patient,
            ...updates,
            _lastModified: Date.now(),
            _syncStatus: 'pending', // Mark as needing sync
        }

        const result = await this.update(updatedPatient)

        // Queue for sync if already synced before
        if (patient._syncStatus === 'synced') {
            try {
                await queuePatientUpdateForSync(result)
            } catch (error) {
                console.error('Failed to queue patient update for sync:', error)
            }
        }

        return result
    }

    async markAsFailed(patient: OfflinePatient, error: string): Promise<OfflinePatient> {
        const updatedPatient: OfflinePatient = {
            ...patient,
            _syncStatus: 'failed',
            _lastModified: Date.now(),
        }

        return this.update(updatedPatient)
    }
}

// Create a singleton instance
export const patientOfflineStore = new PatientOfflineStore() 
